'use client';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { useStoreInfoDrawer } from 'app/[lang]/[esite]/(core)/_header/components/store/store-info-drawer/store-info-drawer-context';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import cx from 'classnames';
import { useAssociatedDealer } from 'components/associated-dealers/associated-dealer-context';
import {
  AdpFormData,
  DropdownValue,
  FormType,
  StoreDropdownValue
} from 'components/associated-dealers/types';
import { DealerLocatorDrawer } from 'components/dealer-locator';
import { DealerLocatorDrawerProvider } from 'components/dealer-locator/dealer-locator-drawer-context';
import { StoreAddress } from 'components/dealer-locator/types';
import {
  AssociatedDealerCheckbox,
  AssociatedDealerDropdown,
  AssociatedDealerForm,
  AssociatedDealerPriceWarning,
  Conditional,
  DrawerBackHeader,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  useBreakpoint
} from 'components/pcc-components';
import ErrorMessageBanner from 'components/error-inline-banner/error-inline-banner';
import {
  CSR_SHARE_FLOW,
  CSR_SHARE_STOREID,
  STATUS_FAIL,
  STATUS_SUCCESS
} from 'constants/analyticsConstants';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { DealerResponse } from 'services/dealer';
import {
  DealerAssociationResponse,
  DealerErrorResponse
} from 'services/dealer-association';
import {
  getDealerCustomersByDCN,
  submitADP,
  submitADPForCSR
} from 'services/dealer-association/actions';
import { DealerCustomer } from 'services/dealer-customer/types';
import {
  CUSTOMER_NUMBER_DROPDOWN_NAME,
  DEALER_DROPDOWN_NAME,
  END_USE_CODE_DROPDOWN_NAME,
  MO_STORE_LOCATION,
  ORDER_TYPE_RADIO_NAME,
  SELECT_CUSTOMER_FORM_ID,
  SKIP_THIS_STEP_CHECKBOX_NAME,
  TERMS_AND_CONDITIONS_CHECKBOX_NAME
} from '../../constants/constants';
import { useDealerCustomer } from '../../hooks/use-dealer-customer';
import {
  getGaEvent,
  getSelectedCustomerNumber,
  getSelectedDealer,
  payloadBuilder,
  sortLabelName
} from '../../utils';
import { AssociatedDealerAddDealerButton } from '../associated-dealer-add-dealer-button';
import { AssociatedDealerTitle } from '../associated-dealer-title';
import { OrderType } from '../order-type';
import { TermsAndConditions } from '../terms-and-condtions';
import styles from './style.module.scss';
import {
  CatIconClose,
  CatIconWarningTriangle,
  CatInlineNotification
} from 'components/blocks-components';
import { setCookie } from 'utils/cookieUtils';
import { MICO_COOKIE } from 'constants/storageConstants';

interface StoreFlags {
  isStoreLocationTandCFlag?: boolean;
  isStoreLocationListViewUpdateFlag?: boolean;
  isStoreLocationMapViewUpdateFlag?: boolean;
  isPCCAssociatedDealerFFEnabled?: boolean;
  isAssociatedDealerModalFlag?: boolean;
  isStoreLocationPinEnhancementFlag?: boolean;
  isStoreLocationOptimizationFlag?: boolean;
  isfullMapViewMobileFlagEnabled?: boolean;
}

interface SelectCustomerFormProps {
  dealerSearchForm: FormType;
  selectCustomerForm: FormType;
  isModal?: boolean;
  isDealerUser: boolean;
  isInstantAccessUser: boolean;
  isCspUser: boolean;
  isLoggedIn: boolean;
  dealerUserResponse?: DealerResponse;
  registeredUserResponse?: DealerAssociationResponse | DealerErrorResponse;
  storeName: string;
  storeAddress: StoreAddress;
  wrongSite?: boolean;
  showSkipThisStep?: boolean;
  showPricingWarning?: boolean;
  storeFlags?: StoreFlags;
  isLoginADP?: boolean;
  isAssociatedDealerModalFlag?: boolean;
  isMobileDrawerActive?: boolean;
  restrictedDealerStores?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedCustomer?: any;
  onCloseModal?: () => void;
  isCsrFlow?: boolean;
}

export const SelectCustomerForm = ({
  dealerSearchForm,
  selectCustomerForm,
  isModal = false,
  isDealerUser,
  isInstantAccessUser,
  isCspUser,
  isLoggedIn,
  dealerUserResponse = null,
  registeredUserResponse = null,
  storeName,
  storeAddress,
  wrongSite = false,
  showSkipThisStep = false,
  showPricingWarning = false,
  storeFlags = null,
  isLoginADP,
  isMobileDrawerActive = false,
  restrictedDealerStores,
  selectedCustomer,
  onCloseModal,
  isCsrFlow = false
}: SelectCustomerFormProps) => {
  const {
    isStoreLocationTandCFlag = false,
    isStoreLocationListViewUpdateFlag = false,
    isStoreLocationMapViewUpdateFlag = false,
    isPCCAssociatedDealerFFEnabled = false,
    isStoreLocationPinEnhancementFlag = false,
    isStoreLocationOptimizationFlag = false,
    isfullMapViewMobileFlagEnabled = false
  } = storeFlags || {};

  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const [customerList, setCustomerList] = useState<DealerCustomer[] | null>([]);
  const [isLocationChangeFailed, setIsLocationChangeFailed] = useState(false);
  const [isSubmitADPForCSRFailed, setIsSubmitADPForCSRFailed] = useState(false);
  const [storeList, setStoreList] = useState<StoreDropdownValue[]>([]);
  const [dealerUserValue, setDealerUserValue] = useState<DropdownValue[]>([]);

  const [dealerUserCustomerName, setDealerUserCustomerName] = useState<
    DropdownValue[]
  >([]);
  const [showWarningCartShare, setShowWarningCartShare] =
    useState<boolean>(false);
  const [isDealerListLoading, setIsDealerListLoading] = useState<boolean>(true);
  const {
    onSuccessHandler,
    showLoader,

    associatedDealerDrawer,
    selectCustomerModal,
    selectCustomerDrawer,
    searchResultsModal,
    searchResultsDrawer
  } = useAssociatedDealer();
  const {
    data,
    stores,
    startSearch,
    orderTypeInfo,
    isLoading,
    isRefetching,
    isError,
    remove,
    isFetched
  } = useDealerCustomer();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const isMobile = !isMD;
  const dealerItemList = useMemo(
    () =>
      registeredUserResponse?.['associations']
        ?.map((association) => {
          return {
            label: association.dealerName,
            value: association.id,
            default: association.isDefault
          };
        })
        .sort(sortLabelName),
    [registeredUserResponse]
  );
  const currentDealerValue = selectCustomerForm.watch(DEALER_DROPDOWN_NAME);
  const storeInfoDrawer = useStoreInfoDrawer();
  const selectedDealer = getSelectedDealer({
    currentDealerValue,
    associations: registeredUserResponse?.['associations']
  });

  const customerNumberList = useMemo(
    () =>
      selectedDealer?.customerNumbersInfo.map((association) => {
        return {
          label: association.name,
          value: association.value,
          default: association.isDefault
        };
      }),
    [selectedDealer?.customerNumbersInfo]
  );

  useEffect(() => {
    if (stores) {
      setStoreList(
        stores.map((store) => {
          return {
            endUseCodes: store.endUseCodes,
            id: store.id,
            default: store.isDefault,
            label: store.label,
            value: store.value
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data]);

  const currentStoreLocationValue = selectCustomerForm.watch(MO_STORE_LOCATION);
  const selectedStoreLocation = storeList?.find(
    (store) => store.label === currentStoreLocationValue
  );
  const selectedStoreLocationValue = storeList?.find(
    (store) => store.label === storeName
  )?.value;
  const endUseCodeList = selectedStoreLocation?.endUseCodes;
  const isSingleDealer = isDealerUser || dealerItemList?.length === 1;
  const isSingleCustomerNumber = customerNumberList?.length === 1;
  const isSingleStore = storeList?.length === 1;
  const isSingleEndUseCode = endUseCodeList?.length === 1;

  const showCustomerNumber = selectedDealer?.isCSP;
  const showEndUseCodeDropdown = endUseCodeList?.length > 0;
  const showOrderType = orderTypeInfo?.length > 1;
  const currentCustomerNumberValue = selectCustomerForm.watch(
    CUSTOMER_NUMBER_DROPDOWN_NAME
  );

  const currentOrderType = selectCustomerForm.watch(ORDER_TYPE_RADIO_NAME);
  const currentEndUseCode = selectCustomerForm.watch(
    END_USE_CODE_DROPDOWN_NAME
  );
  const currentTermsAndConditions = selectCustomerForm.watch(
    TERMS_AND_CONDITIONS_CHECKBOX_NAME
  );
  const selectedCustomerNumber = getSelectedCustomerNumber({
    customerNumberList,
    currentCustomerNumberValue
  });

  const isStoreListEmpty = storeList?.length === 0;
  const isStoreLocationMissing =
    storeList?.length > 0 && !currentStoreLocationValue;
  const isOrderTypeMissing = orderTypeInfo?.length > 1 && !currentOrderType;
  const isEndUseCodeMissing = showEndUseCodeDropdown && !currentEndUseCode;

  const isDisabledContinueButton =
    isError ||
    !currentDealerValue ||
    !currentCustomerNumberValue ||
    isStoreListEmpty ||
    isStoreLocationMissing ||
    isOrderTypeMissing ||
    isEndUseCodeMissing ||
    (!isCsrFlow && !currentTermsAndConditions);

  const prepopulateDealerSelectedInCartShare = useCallback(() => {
    const CSR_SHARE_ID = sessionStorage.getItem(CSR_SHARE_STOREID);
    if (
      dealerItemList &&
      dealerItemList?.length > 0 &&
      CSR_SHARE_ID != currentDealerValue
    ) {
      const findDealerFromShareCartLink = dealerItemList.find(
        (dealerD) => dealerD.value === CSR_SHARE_ID
      );
      if (findDealerFromShareCartLink) {
        selectCustomerForm.setValue(
          DEALER_DROPDOWN_NAME,
          findDealerFromShareCartLink.label
        );
      }
    }
  }, [dealerItemList, currentDealerValue, selectCustomerForm]);

  useEffect(() => {
    if (isDealerUser) {
      setDealerUserValue([
        {
          label: dealerUserResponse.name,
          value: dealerUserResponse.storeId.toString(),
          default: true
        }
      ]);
      setDealerUserCustomerName([
        {
          label: `${data?.customerNumber}- ${data?.dealerCustomerBillingAddress.firstName}`,
          value: data?.customerNumber,
          default: true
        }
      ]);
    }
  }, [
    isDealerUser,
    data?.customerNumber,
    data?.dealerCustomerBillingAddress?.firstName,
    dealerUserResponse
  ]);

  useEffect(() => {
    if (selectedCustomerNumber?.value && selectedDealer?.id) {
      startSearch({
        customerNumber: selectedCustomerNumber.value,
        storeId: selectedDealer.id
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCustomerNumberValue,
    selectedDealer?.id,
    selectedCustomerNumber?.value
  ]);

  useEffect(() => {
    if (dealerItemList && dealerItemList?.length > 0) {
      setIsDealerListLoading(false);
    }
  }, [dealerItemList]);

  const csrShareData = sessionStorage.getItem(CSR_SHARE_FLOW);

  useEffect(() => {
    if (!isDealerListLoading) {
      setShowWarningCartShare(false);
      const CSR_SHARE_ID = sessionStorage.getItem(CSR_SHARE_STOREID);
      if (CSR_SHARE_ID) {
        const findDealerFromShareCartLink = dealerItemList.find(
          (dealerD) => dealerD.value === CSR_SHARE_ID
        );
        if (
          findDealerFromShareCartLink &&
          currentDealerValue != findDealerFromShareCartLink.label &&
          csrShareData
        ) {
          setShowWarningCartShare(true);
        }
      }
    }
  }, [currentDealerValue, isDealerListLoading, dealerItemList, csrShareData]);

  //Submit
  const handleSubmit = () => {
    selectCustomerForm.handleSubmit((data: AdpFormData) => {
      const submitData = payloadBuilder({
        formData: data,
        selectedCustomerNumber,
        selectedStoreLocation,
        endUseCodeList,
        selectedDealer,
        dealerUserValue: dealerUserValue[0],
        dealerUserCustomerName: dealerUserCustomerName[0],
        isDealerUser,
        isLoginAction: isModal,
        loginFromPage: 'loginFromHomePage'
      });
      handlePayload(submitData);
    })();
  };

  const handlePayload = async (submitData) => {
    try {
      let entitlementResponse;
      if (selectedCustomer && isCsrFlow) {
        // Use CSR-specific submit function
        entitlementResponse = await submitADPForCSR(
          submitData,
          selectedCustomer.id
        );
      } else {
        entitlementResponse = await submitADP(submitData, isLoginADP);
      }

      setIsLocationChangeFailed(false);
      setIsSubmitADPForCSRFailed(false);
      showLoader(wrongSite);
      const successEvent = getGaEvent(false, false, STATUS_SUCCESS);
      fireEvent(successEvent);

      if (isLoginADP || isCsrFlow) {
        onSuccessHandler(entitlementResponse);
        if (onCloseModal) {
          onCloseModal();
        }
      } else {
        if (isDealerUser) {
          selectCustomerDrawer.closeDrawer();
          searchResultsDrawer.closeDrawer();
        }

        if (entitlementResponse?.isMicoCookieRequired) {
          // we set the Mico cookie for that change the DCN from the drawer
          setCookie(MICO_COOKIE, 1);
        }
        associatedDealerDrawer.closeDrawer();
      }
    } catch (error) {
      const errorEvent = getGaEvent(false, false, STATUS_FAIL);
      fireEvent(errorEvent);
      if (selectedCustomer && isCsrFlow) {
        setIsSubmitADPForCSRFailed(true);
      } else {
        setIsLocationChangeFailed(true);
      }
    }
  };
  const checkIsPCCAssociatedDealerFFEnabledAndMobile =
    isPCCAssociatedDealerFFEnabled && !isMD && !isDealerUser;
  const handleClose = () => {
    if (isCsrFlow && onCloseModal) {
      onCloseModal();
      return;
    }
    if (checkIsPCCAssociatedDealerFFEnabledAndMobile) {
      associatedDealerDrawer.closeDrawer();
      storeInfoDrawer.openDrawer();
    }
    setCustomerList([]);
    remove();
    isModal
      ? selectCustomerModal.modalControl.close()
      : selectCustomerDrawer.closeDrawer();
  };

  const handleClearSearch = () => {
    setIsLocationChangeFailed(false);
    dealerSearchForm.reset();
    isModal
      ? searchResultsModal.modalControl.close()
      : searchResultsDrawer.closeDrawer();
    handleClose();
  };

  const cn = dealerUserCustomerName?.[0];

  const dealerCustomersByDCN = async (customerName) => {
    if (customerName) {
      try {
        const response = await getDealerCustomersByDCN(
          customerName,
          dealerUserResponse.storeId
        );
        setCustomerList(response.customers);
      } catch (error) {
        setCustomerList(null);
      }
    }
  };
  useEffect(() => {
    if (isDealerUser && cn) {
      dealerCustomersByDCN(cn?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cn]);

  const getDealerListValues = () => {
    return isDealerUser ? dealerUserValue : dealerItemList;
  };
  const drawerTitle = (
    <AssociatedDealerTitle>
      {isDealerUser
        ? replaceTokensInString(
            t('GENERAL_BACK_TO', { 0: t('TITLE_SEARCH_RESULTS') })
          )
        : t('CAT_MA_ASSOCIATEDDEALERS')}
    </AssociatedDealerTitle>
  );
  const isStoreNotAvailable =
    currentCustomerNumberValue &&
    isFetched &&
    !isLoading &&
    (isError || storeList?.length === 0);

  const renderFooterButtons = () => {
    return (
      <div
        className={cx(styles['fitment-border-mobile'], {
          'd-flex justify-content-end':
            checkIsPCCAssociatedDealerFFEnabledAndMobile
        })}
      >
        <CatButton
          variant="primary"
          type="submit"
          disabled={isDisabledContinueButton}
          form={SELECT_CUSTOMER_FORM_ID}
        >
          {t('EF_CONTINUE')}
        </CatButton>
        <Conditional test={isDealerUser}>
          <CatButton variant="ghost" onClick={handleClearSearch}>
            {t('CLEAR_SEARCH')}
          </CatButton>
        </Conditional>
      </div>
    );
  };

  const renderSkipADPCheckbox = () => (
    <Conditional test={isMD || (!isMD && !isPCCAssociatedDealerFFEnabled)}>
      <div className="mb-3">
        <Conditional test={!isDealerUser}>
          <AssociatedDealerCheckbox
            name={SKIP_THIS_STEP_CHECKBOX_NAME}
            label={t('SHOP_PREF_SKIP_ADP')}
            fieldNote={t('SHOP_PREF_SKIP_ADP2')}
            checked={false}
            required={false}
            showCheckbox={showSkipThisStep}
          />
        </Conditional>
      </div>
    </Conditional>
  );

  return (
    <DealerLocatorDrawerProvider
      isStoreLocationTandCFlag={isStoreLocationTandCFlag}
      isStoreLocationListViewUpdateFlag={isStoreLocationListViewUpdateFlag}
      isStoreLocationMapViewUpdateFlag={isStoreLocationMapViewUpdateFlag}
      isStoreLocationPinEnhancementFlag={isStoreLocationPinEnhancementFlag}
      isfullMapViewMobileFlagEnabled={isfullMapViewMobileFlagEnabled}
      isStoreLocationOptimizationFlag={isStoreLocationOptimizationFlag}
    >
      <DealerLocatorDrawer
        autoSearch={!isCspUser && (!isLoggedIn || isInstantAccessUser)}
        isADP
        storeAddress={storeAddress}
        restrictedDealerStores={restrictedDealerStores}
      />
      <Conditional
        test={isDealerUser || checkIsPCCAssociatedDealerFFEnabledAndMobile}
        fallback={
          <DrawerHeader
            className={`${isCsrFlow ? 'd-flex w-100 justify-content-between align-items-center' : ''}`}
          >
            {drawerTitle}
            <Conditional test={isCsrFlow}>
              <CatButton
                variant="ghost"
                onClick={handleClose}
                data-testid="csr-close-button"
              >
                <CatIconClose />
              </CatButton>
            </Conditional>
          </DrawerHeader>
        }
      >
        <DrawerBackHeader
          hideBackButton={isMobileDrawerActive && !isMD}
          handleClose={handleClose}
        >
          {drawerTitle}
        </DrawerBackHeader>
      </Conditional>
      <DrawerBody
        className={cx({
          [styles['drawer-body']]: checkIsPCCAssociatedDealerFFEnabledAndMobile
        })}
      >
        <div>
          <Conditional test={showWarningCartShare}>
            <CatInlineNotification
              variant="warning"
              heading={t('LOSS_CART_ITEMS_WARNING')}
              data-testid="manual-approval-message-share-cart"
            >
              <CatIconWarningTriangle
                slot="before"
                data-testid="warning-icon"
              />
              {t('LOSS_CART_ITEMS_WARNING_VERBIAGE')}
              <div slot="after">
                <CatButton onClick={prepopulateDealerSelectedInCartShare}>
                  {t('SHIP_GO_BACK')}
                </CatButton>
              </div>
            </CatInlineNotification>
          </Conditional>
          <Conditional
            test={
              !isDealerUser &&
              showPricingWarning &&
              !checkIsPCCAssociatedDealerFFEnabledAndMobile
            }
          >
            <AssociatedDealerPriceWarning
              title={t('CAT_PRICE_MSG')}
              message={t('CAT_PROMO_MSG')}
            />
          </Conditional>
          <Conditional test={customerList === null}>
            <AssociatedDealerPriceWarning
              title={t('DEALER_USER_NO_REGISTERED_DCN')}
              message={t('DEALER_USER_ASSIST_DCN')}
            />
          </Conditional>
          <Conditional
            test={
              (isError && !isStoreNotAvailable) ||
              registeredUserResponse?.isError ||
              isSubmitADPForCSRFailed
            }
          >
            <ErrorMessageBanner heading={''} ignoreScroll={true}>
              <span className="align-middle cat-u-theme-typography-body-sm u-color--ExtraDarkGray">
                {t('UNKNOWN_ERROR_MESSAGE')}
              </span>
            </ErrorMessageBanner>
          </Conditional>
          <Conditional test={isStoreNotAvailable}>
            <ErrorMessageBanner heading={''} ignoreScroll={true}>
              <span className="align-middle cat-u-theme-typography-body-sm u-color--ExtraDarkGray">
                {t('STORE_NOT_AVAILBALE_ERROR')}
              </span>
            </ErrorMessageBanner>
          </Conditional>
          <Conditional test={isLocationChangeFailed}>
            <AssociatedDealerPriceWarning
              title={t('TECH_ISSUE_CART_TITLE')}
              message={t('TECH_ISSUE_EMPTY_CART')}
            />
          </Conditional>
          <AssociatedDealerForm
            id={SELECT_CUSTOMER_FORM_ID}
            formMethods={selectCustomerForm}
            onSubmit={handleSubmit}
          >
            <Conditional test={!checkIsPCCAssociatedDealerFFEnabledAndMobile}>
              <div className="mt-3">
                <AssociatedDealerDropdown
                  isPCCAssociatedDealerFFEnabled={
                    isPCCAssociatedDealerFFEnabled
                  }
                  isMobile={isMobile}
                  isModal={isModal}
                  name={DEALER_DROPDOWN_NAME}
                  placeholder={t('CAT_MA_SELECTDEALER')}
                  label={t('Footer_Dealer')}
                  itemList={getDealerListValues()}
                  readOnly={isSingleDealer}
                  required={true}
                  requiredNotation={t('REQUIRED_FIELD_MESSAGE')}
                  optionalNotation={t('OPTIONAL').toLowerCase()}
                />
              </div>
              <Conditional test={!isDealerUser}>
                <AssociatedDealerAddDealerButton />
              </Conditional>
            </Conditional>
            <Conditional test={checkIsPCCAssociatedDealerFFEnabledAndMobile}>
              <AssociatedDealerDropdown
                isPCCAssociatedDealerFFEnabled={isPCCAssociatedDealerFFEnabled}
                isMobile={isMobile}
                isModal={isModal}
                name={DEALER_DROPDOWN_NAME}
                placeholder={t('CAT_MA_SELECTDEALER')}
                label={t('Footer_Dealer')}
                itemList={getDealerListValues()}
                readOnly={isSingleDealer}
                isHeadless={true}
                isPCCAssociatedDealerFFEnabledAndMobile={true}
                required={true}
                requiredNotation={t('REQUIRED_FIELD_MESSAGE')}
                optionalNotation={t('OPTIONAL').toLowerCase()}
              />
              <Conditional
                test={!isDealerUser && !isPCCAssociatedDealerFFEnabled}
              >
                <AssociatedDealerAddDealerButton />
              </Conditional>
            </Conditional>
            <div className="mt-3">
              <AssociatedDealerDropdown
                isPCCAssociatedDealerFFEnabled={isPCCAssociatedDealerFFEnabled}
                isMobile={isMobile}
                isModal={isModal}
                name={CUSTOMER_NUMBER_DROPDOWN_NAME}
                placeholder={t('CAT_MA_SELECTUCID')}
                label={t('CUST_NAME_NUMBER_COMBINED')}
                itemList={
                  isDealerUser ? dealerUserCustomerName : customerNumberList
                }
                readOnly={
                  isDealerUser || (isSingleCustomerNumber && showCustomerNumber)
                }
                showDropdown={showCustomerNumber}
                required={true}
                requiredNotation={t('REQUIRED_FIELD_MESSAGE')}
                optionalNotation={t('OPTIONAL').toLowerCase()}
              />
            </div>
            <div className="mt-3">
              <AssociatedDealerDropdown
                isPCCAssociatedDealerFFEnabled={isPCCAssociatedDealerFFEnabled}
                isMobile={isMobile}
                isModal={isModal}
                name={MO_STORE_LOCATION}
                placeholder={t('CAT_MA_SELECTDEALAERSTORE')}
                label={t('MO_STORE_LOCATION')}
                itemList={storeList}
                readOnly={isSingleStore}
                loading={isLoading || isRefetching}
                required={true}
                showDropdown={!isStoreNotAvailable}
                selectedValue={selectedStoreLocationValue}
                requiredNotation={t('REQUIRED_FIELD_MESSAGE')}
                optionalNotation={t('OPTIONAL').toLowerCase()}
              />
            </div>
            <Conditional test={showOrderType}>
              <div className="mt-3">
                <OrderType orderTypeInfo={orderTypeInfo} />
              </div>
            </Conditional>
            <div className="mt-3">
              <AssociatedDealerDropdown
                isPCCAssociatedDealerFFEnabled={isPCCAssociatedDealerFFEnabled}
                isMobile={isMobile}
                isModal={isModal}
                name={END_USE_CODE_DROPDOWN_NAME}
                placeholder={t('CAT_SHOPPING_PREFERENCES_SEL_END_USE_CODE')}
                label={t('CAT_SHOPPING_PREFERENCES_END_USE_CODE')}
                itemList={endUseCodeList}
                readOnly={isSingleEndUseCode}
                required={true}
                showDropdown={showEndUseCodeDropdown}
                requiredNotation={t('REQUIRED_FIELD_MESSAGE')}
                optionalNotation={t('OPTIONAL').toLowerCase()}
              />
            </div>
            {renderSkipADPCheckbox()}
            <Conditional
              test={!isDealerUser && !isMD && isPCCAssociatedDealerFFEnabled}
            >
              <AssociatedDealerAddDealerButton
                variant="ghost"
                data-testid="associateDealerButton"
              />
            </Conditional>
          </AssociatedDealerForm>
        </div>

        <Conditional test={isPCCAssociatedDealerFFEnabled}>
          <div>
            <FormProvider {...selectCustomerForm}>
              <Conditional test={!isDealerUser && !isMD}>
                <AssociatedDealerCheckbox
                  name={SKIP_THIS_STEP_CHECKBOX_NAME}
                  label={t('SHOP_PREF_SKIP_ADP')}
                  fieldNote={t('SHOP_PREF_SKIP_ADP2')}
                  checked={false}
                  required={false}
                  showCheckbox={showSkipThisStep}
                  className={cx({
                    [styles['skip-this-step']]: isPCCAssociatedDealerFFEnabled
                  })}
                />
              </Conditional>
              <Conditional
                test={
                  !isDealerUser &&
                  showPricingWarning &&
                  checkIsPCCAssociatedDealerFFEnabledAndMobile &&
                  !isLoginADP
                }
              >
                <AssociatedDealerPriceWarning
                  title={t('CAT_PRICE_MSG')}
                  message={t('CAT_PROMO_MSG')}
                  isPCCAssociatedDealerFFEnabledAndMobile={
                    checkIsPCCAssociatedDealerFFEnabledAndMobile
                  }
                />
              </Conditional>
              <div className="mt-3">
                <TermsAndConditions
                  selectedDealer={selectedDealer}
                  dealerUserResponse={dealerUserResponse}
                  isDealerUser={isDealerUser}
                />
              </div>
            </FormProvider>
          </div>
        </Conditional>
      </DrawerBody>
      <DrawerFooter>
        <Conditional test={!isPCCAssociatedDealerFFEnabled && !isCsrFlow}>
          <FormProvider {...selectCustomerForm}>
            <TermsAndConditions
              selectedDealer={selectedDealer}
              dealerUserResponse={dealerUserResponse}
              isDealerUser={isDealerUser}
            />
          </FormProvider>
        </Conditional>
        {renderFooterButtons()}
      </DrawerFooter>
    </DealerLocatorDrawerProvider>
  );
};
